import { useState, useSyncExternalStore } from 'react';
import { AlertDialog as BaseAlertDialog } from '@base-ui/react/alert-dialog';
import { RiDeleteBinLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { Button } from './button';
import { renderIcon, type IconLike } from './icon';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { overlayBackdrop, overlayPopupMotion, overlayViewport } from './modal';

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactElement;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** `danger` — red icon and a Destructive confirm button. */
  tone?: 'default' | 'danger';
  /** Leading glyph (24). Defaults per tone: information-line / error-warning-line. `null` hides it. */
  icon?: IconLike | null;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** May return a promise: the button shows a spinner and the dialog closes when it resolves. */
  onConfirm?: () => unknown;
  onCancel?: () => void;
  /** External loading state (alternative to returning a promise). */
  loading?: boolean;
  container?: PortalContainer;
}

/**
 * Confirmation dialog — kit Alert Dialog 67:12233: w464, r4, `bg-panel`, 1px White/10, shadow-toast.
 * Content padding 12/16, gap 12: icon 24 + title 600 16/24 + text 400 14/20 White/60.
 * Footer padding 12/16, gap 8, top line: Secondary + Primary (Destructive when `tone="danger"`).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  tone = 'default',
  icon,
  confirmLabel = tone === 'danger' ? "O'chirish" : 'Tasdiqlash',
  cancelLabel = 'Bekor qilish',
  onConfirm,
  onCancel,
  loading,
  container,
}: ConfirmDialogProps) {
  const portalContainer = usePortalContainer(container);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (open === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const glyph = icon === null ? null : (icon ?? (tone === 'danger' ? RiErrorWarningLine : RiInformationLine));
  const busy = pending || Boolean(loading);

  const confirm = async () => {
    const result = onConfirm?.();
    if (result instanceof Promise) {
      setPending(true);
      try {
        await result;
        setOpen(false);
      } catch {
        /* keep the dialog open; the caller reports the error (toast) */
      } finally {
        setPending(false);
      }
    } else if (!loading) {
      setOpen(false);
    }
  };

  return (
    <BaseAlertDialog.Root
      open={isOpen}
      onOpenChange={(next) => {
        if (busy && !next) return;
        if (!next) onCancel?.();
        setOpen(next);
      }}
    >
      {trigger && <BaseAlertDialog.Trigger render={trigger} />}
      <BaseAlertDialog.Portal container={portalContainer}>
        <BaseAlertDialog.Backdrop className={overlayBackdrop} />
        <BaseAlertDialog.Viewport className={overlayViewport}>
          <BaseAlertDialog.Popup
            className={cn(
              'flex w-464 max-w-full flex-col rounded-4 border border-line bg-panel text-fg shadow-toast outline-none',
              overlayPopupMotion,
            )}
          >
            <div className="flex items-start gap-12 px-16 py-12">
              {glyph && (
                <span className={cn('flex h-24 shrink-0 items-center', tone === 'danger' ? 'text-tone-red-lighter' : 'text-icon')}>
                  {renderIcon(glyph, 'size-24')}
                </span>
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <BaseAlertDialog.Title className="text-base font-semibold text-fg">{title}</BaseAlertDialog.Title>
                {description && (
                  <BaseAlertDialog.Description className="text-sm text-fg-secondary">{description}</BaseAlertDialog.Description>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-8 border-t border-line px-16 py-12">
              <BaseAlertDialog.Close render={<Button variant="secondary" disabled={busy} />}>{cancelLabel}</BaseAlertDialog.Close>
              <Button
                variant={tone === 'danger' ? 'destructive' : 'primary'}
                leftIcon={tone === 'danger' ? RiDeleteBinLine : undefined}
                loading={busy}
                onClick={confirm}
              >
                {confirmLabel}
              </Button>
            </div>
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Viewport>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Imperative API:  if (await confirm({ title: "Bo'lim o'chirilsinmi?", tone: 'danger' })) …
 * Mount <ConfirmDialogHost /> once (app shell).
 * --------------------------------------------------------------------------------------------- */

type ConfirmRequest = Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'trigger' | 'onConfirm' | 'onCancel' | 'loading' | 'container'> & {
  id: number;
  resolve: (ok: boolean) => void;
};

let queue: ConfirmRequest[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function confirm(options: Omit<ConfirmRequest, 'id' | 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => {
    queue = [...queue, { ...options, id: nextId++, resolve }];
    emit();
  });
}

export function ConfirmDialogHost({ container }: { container?: PortalContainer }) {
  const current = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => queue[0],
    () => undefined,
  );
  if (!current) return null;
  const finish = (ok: boolean) => {
    current.resolve(ok);
    queue = queue.filter((r) => r.id !== current.id);
    emit();
  };
  const { id, resolve: _resolve, ...props } = current;
  return (
    <ConfirmDialog
      key={id}
      {...props}
      container={container}
      open
      onOpenChange={(open) => {
        if (!open) finish(false);
      }}
      onConfirm={() => finish(true)}
    />
  );
}
